import { apiIntegrations } from "@/api/integrations/integration.api.ts";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import {
  useModals,
  useToasts,
  Box,
  Button,
  ListItem,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmailServersSettingsEditModal } from "@/pages/me/widgets/EmailServersSettingsEditModal.tsx";
import {
  useEmailDisconnect,
  useEmailIntegrations,
} from "@/queries/integrations/use-emails.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiIntegration, ApiIntegrationType } from "@/types/integrations/integration.types.ts";
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export function EmailIntegrations() {
  const { t } = useTranslation();
  const { renderDateAndTime } = useFormatter();
  const { handleErrors } = useErrorHandle();
  const { showInfo, showError } = useToasts();
  const { openModal } = useModals();

  const { data: integrations, isLoading } = useEmailIntegrations();
  const emailDisconnect = useEmailDisconnect();

  const [isConnecting, setIsConnecting] = useState(false);

  const handleGmailConnect = useCallback(async () => {
    analyticsService.trackEvent(analyticEvents.pageAction, {
      [analyticProperties.actionType]: "Connect Email Started",
      [analyticProperties.source]: "Gmail",
    });

    setIsConnecting(true);
    const { data, error } = await apiIntegrations.install(ApiIntegrationType.gmail);
    setIsConnecting(false);

    if (data) {
      window.location.href = data.authorization_url;
    } else handleErrors(error);
  }, []);

  const handleOutlookConnect = useCallback(async () => {
    analyticsService.trackEvent(analyticEvents.pageAction, {
      [analyticProperties.actionType]: "Connect Email Started",
      [analyticProperties.source]: "Outlookt",
    });

    setIsConnecting(true);
    const { data, error } = await apiIntegrations.install(ApiIntegrationType.outlook);
    setIsConnecting(false);

    if (data) {
      window.location.href = data.authorization_url;
    } else handleErrors(error);
  }, []);

  const handleEmailServersModify = useCallback((integration?: ApiIntegration) => {
    analyticsService.trackEvent(analyticEvents.pageAction, {
      [analyticProperties.actionType]: "Connect Email Servers Started",
      [analyticProperties.source]: "Email Servers",
    });

    openModal(EmailServersSettingsEditModal, { integration });
  }, []);

  const handleEmailDisconnect = useCallback(async (integrationId: number) => {
    emailDisconnect.mutate(integrationId, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.email.disconnected, {
          [analyticProperties.id]: integrationId,
        });
        showInfo({
          title: t("integrations:account_disconnected"),
        });
      },
    });
  }, []);

  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");

  useEffect(() => {
    if (error) {
      showError({
        title: t(`api_errors:${error}`),
      });
    }
  }, [error]);

  const gmailIntegrations = useMemo(() => {
    return (
      integrations?.filter(
        (integration) => integration.integration_type === ApiIntegrationType.gmail
      ) || []
    );
  }, [integrations]);

  const outlookIntegrations = useMemo(() => {
    return (
      integrations?.filter(
        (integration) => integration.integration_type === ApiIntegrationType.outlook
      ) || []
    );
  }, [integrations]);

  const smtpIntegrations = useMemo(() => {
    return (
      integrations?.filter(
        (integration) => integration.integration_type === ApiIntegrationType.smtp
      ) || []
    );
  }, [integrations]);

  const renderConnectedAccount = (integration: ApiIntegration, actions?: ReactNode) => {
    return (
      <ListItem
        title={integration.identity}
        caption={
          integration.datetime_last_check
            ? `${t("integrations:last_synced")}: ${renderDateAndTime(integration.datetime_last_check)}`
            : undefined
        }
        captionSlot={
          integration.is_active ? null : (
            <Text className={"text-warning"}>{t("common:disabled")}</Text>
          )
        }
        valueSlot={
          actions ? (
            actions
          ) : (
            <Button
              size={"sm"}
              onClick={() => {
                handleEmailDisconnect(integration.id);
              }}
              disabled={isConnecting}
              variant={"secondary"}
            >
              {t("integrations:disconnect")}
            </Button>
          )
        }
      />
    );
  };

  return (
    <Stack className={"card-panel-sm"} gap={"2xl"}>
      {isLoading && <Skeleton className={"w-full h-10"} />}

      <Title header={t("core:email_provider")} caption={t("core:email_provider_caption")} />

      <Stack>
        <Title header={"Gmail"} size={"xs"} paddingBottom caption={"Google"} />
        {gmailIntegrations.length > 0 ? (
          <Stack>
            {gmailIntegrations.map((integration) => (
              <Fragment key={`integration-${integration.id}`}>
                {renderConnectedAccount(integration)}
              </Fragment>
            ))}
            <Box className={"mt-2"}>
              <DemoUnavailable showTag={false}>
                <Button
                  variant="secondary"
                  size={"sm"}
                  onClick={handleGmailConnect}
                  disabled={isConnecting}
                >
                  {t("integrations:add_another_account")}
                </Button>
              </DemoUnavailable>
            </Box>
          </Stack>
        ) : (
          <ListItem
            captionSlot={
              <Text className={"font-semibold text-warning"}>
                {t("integrations:not_connected")}
              </Text>
            }
            valueSlot={
              <DemoUnavailable showTag={false}>
                <Button
                  variant="secondary"
                  size={"sm"}
                  onClick={handleGmailConnect}
                  disabled={isConnecting}
                >
                  {t("integrations:connect")}
                </Button>
              </DemoUnavailable>
            }
          />
        )}

        <Title
          header={"Outlook"}
          size={"xs"}
          paddingBottom
          paddingTop
          caption={"Microsoft Exchange"}
        />
        {outlookIntegrations.length > 0 ? (
          <Stack>
            {outlookIntegrations.map((integration) => (
              <Fragment key={`integration-${integration.id}`}>
                {renderConnectedAccount(integration)}
              </Fragment>
            ))}
            <Box className={"mt-2"}>
              <DemoUnavailable showTag={false}>
                <Button variant="secondary" size={"sm"} onClick={handleOutlookConnect}>
                  {t("integrations:add_another_account")}
                </Button>
              </DemoUnavailable>
            </Box>
          </Stack>
        ) : (
          <ListItem
            captionSlot={
              <Text className={"font-semibold text-warning"}>
                {t("integrations:not_connected")}
              </Text>
            }
            valueSlot={
              <DemoUnavailable showTag={false}>
                <Button variant="secondary" size={"sm"} onClick={handleOutlookConnect}>
                  {t("integrations:connect")}
                </Button>
              </DemoUnavailable>
            }
          />
        )}
        <Title
          header={t("core:email_servers_settings")}
          size={"xs"}
          paddingBottom
          paddingTop
          caption={t("core:email_servers_provider_caption")}
        />
        {smtpIntegrations.length > 0 ? (
          <Stack>
            {smtpIntegrations.map((integration) => (
              <Fragment key={`integration-${integration.id}`}>
                {renderConnectedAccount(
                  integration,
                  <DemoUnavailable showTag={false}>
                    <Button
                      size={"sm"}
                      onClick={() => {
                        handleEmailServersModify(integration);
                      }}
                      disabled={isConnecting}
                      variant={"secondary"}
                    >
                      {t("common:edit")}
                    </Button>
                  </DemoUnavailable>
                )}
              </Fragment>
            ))}
            <Box className={"mt-2"}>
              <DemoUnavailable showTag={false}>
                <Button
                  size={"sm"}
                  variant="secondary"
                  onClick={() => {
                    handleEmailServersModify();
                  }}
                >
                  {t("integrations:add_another_account")}
                </Button>
              </DemoUnavailable>
            </Box>
          </Stack>
        ) : (
          <ListItem
            captionSlot={
              <Text className={"font-semibold text-warning"}>
                {t("integrations:not_connected")}
              </Text>
            }
            valueSlot={
              <DemoUnavailable showTag={false}>
                <Button
                  size={"sm"}
                  variant="secondary"
                  onClick={() => {
                    handleEmailServersModify();
                  }}
                  disabled={isConnecting}
                >
                  {t("integrations:connect")}
                </Button>
              </DemoUnavailable>
            }
          />
        )}
      </Stack>
    </Stack>
  );
}
